import {
  ArrowRightOutlined,
  MinusOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Heading, Tooltip } from '@mm/components';
import { uniqueId } from 'lodash';
import React, { ReactNode, useState } from 'react';
import { BookDemoButton } from '../../../../../../common/buttons/BookDemoButton/BookDemoButton';
import { useBookDemo } from '../../../../../../common/buttons/BookDemoButton/use-book-demo';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import classNames from 'classnames';
import css from './ActivationBlock.module.css';
import { IN_APP_DEMO_UTM } from '../../../../../../dashboard/pages/XRayOnboarding/WebsiteReportSummary/constants';
import { useActivationSteps } from './use-activation-steps';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export type ActionProps = {
  keyName: string;
  icon: ReactNode;
  buttonName?: string;
  tooltip?: string;
  title: string;
  buttonClassName?: string;
  onClick?: () => void;
  hide?: boolean;
  component?: ReactNode;
  disabled?: boolean;
  completed?: boolean;
};

export const ActivationAction = ({
  keyName,
  icon,
  title,
  buttonName,
  tooltip,
  buttonClassName,
  onClick,
  component,
  disabled,
  completed,
}: ActionProps) => {
  const completedNotDisabled = completed && !disabled;
  const { pixelName = 'X-Ray Pixel' } = useWhitelabelConfig();
  const isXray = keyName === 'xray';

  return (
    <div
      className={classNames(css.channelContainer, {
        [css.completedAction]: completed,
        [css.xrayAction]: isXray && !completedNotDisabled,
      })}
      onClick={e => {
        e.preventDefault();
      }}
    >
      {completed && <div className={css.doneTag}>Done</div>}

      <div
        className={classNames(css.content, {
          [css.xrayContent]: isXray && !completedNotDisabled,
        })}
      >
        {icon}
        <span className={css.contentTitle}>{title}</span>
        {component}
      </div>

      <PrimaryButton
        className={classNames(css.actionButton, buttonClassName, {
          [css.completedBtn]: completed,
          [css.xrayActionButton]: isXray && !completedNotDisabled,
        })}
        onClick={e => {
          e?.stopPropagation();
          onClick?.();
        }}
        disabled={disabled}
        color={completed ? 'transparent-no-shadow' : 'blue'}
      >
        {completed ? (
          <Tooltip
            title={buttonName}
            className={css.buttonTooltip}
            placement="bottom"
          >
            {buttonName}
          </Tooltip>
        ) : (
          <Tooltip
            title={disabled ? `Install ${pixelName}` : ''}
            className={css.buttonTooltip}
            placement="bottom"
          >
            {buttonName ? (
              buttonName
            ) : (
              <ArrowRightOutlined className={css.rightArrow} />
            )}
          </Tooltip>
        )}
      </PrimaryButton>
    </div>
  );
};

export const ActivationBlock = ({ className }: { className?: string }) => {
  const { actions } = useActivationSteps();

  const { isEligibleForDemo } = useBookDemo();

  const activeActions = actions.filter(action => !action.hide);

  const completedActionsCount = activeActions
    .map(a => a.completed)
    .filter(Boolean).length;

  const [listCollapsed, setListCollapsed] = useState(true);
  const collapserButtonName = listCollapsed
    ? completedActionsCount === 0
      ? 'Get Started'
      : 'Continue Setup'
    : 'Collapse';
  const hasCompletedAllActions = completedActionsCount === activeActions.length;

  const headerText = hasCompletedAllActions
    ? 'Account Setup is Completed  🎉'
    : 'Complete Account Setup';

  if (hasCompletedAllActions) {
    return <></>;
  }

  return (
    <div className={classNames(css.container, className)}>
      <Heading
        size={2}
        className={css.heading}
        onClick={() => setListCollapsed(!listCollapsed)}
      >
        <div className={css.headerRow}>
          {headerText}{' '}
          <div className={css.progressContainer}>
            <div className={css.progress}>
              {activeActions.map((action, idx) => (
                <span
                  className={classNames(css.progressItem, {
                    [css.progressItemCompleted]: idx < completedActionsCount,
                  })}
                  key={action.keyName}
                />
              ))}
            </div>{' '}
            <span>
              {completedActionsCount}/{activeActions.length} steps
            </span>
          </div>
          <PrimaryButton
            onClick={() => setListCollapsed(!listCollapsed)}
            className={classNames(css.collapser, {
              [css.completedBtn]: !listCollapsed,
            })}
            color={listCollapsed ? 'blue' : 'transparent-no-shadow'}
            leftIcon={!listCollapsed && <UpOutlined />}
          >
            {collapserButtonName}
          </PrimaryButton>
        </div>
      </Heading>
      <div className={css.blocks}>
        {!listCollapsed &&
          activeActions.map(action => (
            <ActivationAction key={uniqueId()} {...action} />
          ))}
      </div>
      {isEligibleForDemo && (
        <div className={css.footer}>
          <div>Talk to an expert to take advantage of all our features</div>
          <BookDemoButton className={css.bookDemo} utm={IN_APP_DEMO_UTM} />
        </div>
      )}
    </div>
  );
};
